import axios from "axios";

// Same-origin hostnames — when the frontend is served from one of these,
// API calls go to the SAME origin (relative URLs) rather than the
// REACT_APP_BACKEND_URL baked into the bundle. This sidesteps CORS
// entirely on production domains. Add new domains here as the brand rolls
// out more hosts.
const SAME_ORIGIN_HOSTS = [
  /\.emergent\.host$/i,
  /^contact\.swisswatch\.com\.my$/i,
];
const isSameOriginHost = typeof window !== "undefined" &&
  SAME_ORIGIN_HOSTS.some((re) => re.test(window.location.hostname));
const BASE = isSameOriginHost ? "" : (process.env.REACT_APP_BACKEND_URL || "");

// Shared base for direct browser navigations (e.g. window.open CSV export)
// that cannot go through axios. Same origin rule as the clients above, so
// single-origin hosting works without a baked-in REACT_APP_BACKEND_URL.
export const apiBase = `${BASE}/api`;

// Public endpoints — no credentials needed, allows wildcard CORS.
export const api = axios.create({ baseURL: `${BASE}/api` });

// Admin endpoints — uses cookie-based JWT, requires credentials.
const adminAxios = axios.create({ baseURL: `${BASE}/api`, withCredentials: true });

// Staff endpoints — magic-link cookie session.
const teamAxios = axios.create({ baseURL: `${BASE}/api`, withCredentials: true });

export const adminApi = {
  login: (email, password) => adminAxios.post("/admin/login", { email, password }),
  logout: () => adminAxios.post("/admin/logout"),
  me: () => adminAxios.get("/admin/me"),
  dashboard: () => adminAxios.get("/admin/dashboard"),
  analytics: (params) => adminAxios.get("/admin/analytics", { params }),
  analyticsMarketing: (params) => adminAxios.get("/admin/analytics/marketing", { params }),
  analyticsCapi: (params) => adminAxios.get("/admin/analytics/capi", { params }),
  // Pass 58 — channel P&L spend inputs.
  manualSpend: (body) => adminAxios.post("/admin/channels/manual_spend", body),
  metaSync: (body) => adminAxios.post("/admin/channels/meta_sync", body),
  leads: (params) => adminAxios.get("/admin/leads", { params }),
  manualLead: (body) => adminAxios.post("/admin/leads/manual", body),
  leadDetail: (id) => adminAxios.get(`/admin/leads/${id}`),
  leadUpdate: (id, body) => adminAxios.patch(`/admin/leads/${id}`, body),
  team: () => adminAxios.get("/admin/team"),
  teamCreate: (body) => adminAxios.post("/admin/team", body),
  teamUpdate: (id, body) => adminAxios.patch(`/admin/team/${id}`, body),
  teamDelete: (id) => adminAxios.delete(`/admin/team/${id}`),
  duty: (params) => adminAxios.get("/admin/duty", { params }),
  dutyAssign: (body) => adminAxios.post("/admin/duty", body),
  dutyAutofill: (body) => adminAxios.post("/admin/duty/autofill", body),
  dutyDelete: (date) => adminAxios.delete(`/admin/duty/${date}`),
  offdays: () => adminAxios.get("/admin/offdays"),
  offdayCreate: (body) => adminAxios.post("/admin/offdays", body),
  offdayDelete: (date) => adminAxios.delete(`/admin/offdays/${date}`),
  // Pass 60 — planned leave on the duty calendar.
  leave: () => adminAxios.get("/admin/leave"),
  leaveCreate: (body) => adminAxios.post("/admin/leave", body),
  leaveCancel: (id) => adminAxios.delete(`/admin/leave/${id}`),
  settings: () => adminAxios.get("/admin/settings"),
  settingsPatch: (body) => adminAxios.patch("/admin/settings", body),
  passwordChange: (body) => adminAxios.post("/admin/settings/password", body),
  // Pass 45 — review-request workflow (admin sees ALL salesmen's queues).
  reviews: () => adminAxios.get("/admin/reviews"),
  reviewSend: (id, body) => adminAxios.post(`/admin/reviews/${id}/send`, body),
  leadHistory: (id) => adminAxios.get(`/admin/leads/${id}/history`),
  reviewSkip: (id) => adminAxios.post(`/admin/reviews/${id}/skip`),
  // Pass 47 — two-step email confirm flow.
  reviewEmailPreview: (id, body) => adminAxios.post(`/admin/reviews/${id}/email_preview`, body),
};

export const teamApi = {
  login: (email) => teamAxios.post("/team/login", { email }),
  auth: (token) => teamAxios.post("/team/auth", { token }),
  me: () => teamAxios.get("/team/me"),
  logout: () => teamAxios.post("/team/logout"),
  dashboard: () => teamAxios.get("/team/dashboard"),
  leads: (params) => teamAxios.get("/team/leads", { params }),
  leadDetail: (id) => teamAxios.get(`/team/leads/${id}`),
  leadUpdate: (id, body) => teamAxios.patch(`/team/leads/${id}`, body),
  duty: (params) => teamAxios.get("/team/duty", { params }),
  leave: (params) => teamAxios.get("/team/leave", { params }),
  leaveCreate: (body) => teamAxios.post("/team/leave", body),
  leaveCancel: (id) => teamAxios.delete(`/team/leave/${id}`),
  manualLead: (body) => teamAxios.post("/team/leads/manual", body),
  // Pass 45 — review-request workflow (scoped to Duty_Person == me).
  reviews: () => teamAxios.get("/team/reviews"),
  reviewSend: (id, body) => teamAxios.post(`/team/reviews/${id}/send`, body),
  leadHistory: (id) => teamAxios.get(`/team/leads/${id}/history`),
  reviewSkip: (id) => teamAxios.post(`/team/reviews/${id}/skip`),
  // Pass 47 — two-step email confirm flow.
  reviewEmailPreview: (id, body) => teamAxios.post(`/team/reviews/${id}/email_preview`, body),
};

// Hot read-only public endpoints get a shared in-flight promise +
// short response cache so the 3-way fan-out on every page mount
// (PublicLayout, BrandHeader, route page) collapses to one network
// request. See backend `_AsyncSingleflightCache` for the matching
// server-side guard.
function _makeSingleflight(loader, ttlMs = 30_000) {
  const entries = new Map();   // key -> { value, cachedAt }
  const inflight = new Map();  // key -> pending promise
  return (key = "_") => {
    const now = Date.now();
    const hit = entries.get(key);
    if (hit && now - hit.cachedAt < ttlMs) return Promise.resolve(hit.value);
    const pending = inflight.get(key);
    if (pending) return pending;
    const p = loader(key)
      .then((r) => {
        entries.set(key, { value: r, cachedAt: Date.now() });
        return r;
      })
      .finally(() => {
        inflight.delete(key);
      });
    inflight.set(key, p);
    return p;
  };
}

const _siteConfigFetch = _makeSingleflight(() => api.get("/site/config"));
// Calendar is keyed by YYYY-MM — different months get their own cache
// entry. Same shared-promise behaviour per month.
const _calendarFetch = _makeSingleflight((key) => {
  // key encodes "STORE:YYYY-MM" so KL and JB calendars never share
  // a cache slot. See backend public_calendar — same key shape.
  const [store, month] = key.split(":");
  return api.get("/public/calendar", { params: { month, store } });
});

export const publicApi = {
  config: () => _siteConfigFetch(),
  calendar: (month, store = "KL") => _calendarFetch(`${store}:${month}`),
  timeslots: (date, store = "KL") => api.get("/public/timeslots", { params: { date, store } }),
  email: (body) => api.post("/leads/email", body),
  whatsapp: (body) => api.post("/leads/whatsapp", body),
  appointment: (body) => api.post("/leads/appointment", body),
  walkin: (body) => api.post("/walkin", body),
  // Checks the PIN on the server and, only if it is correct, returns the
  // staff list and today's duty person (Security Phase 1b).
  walkinUnlock: (pin) => api.post("/walkin/unlock", { pin }),
  // Staff one-click update link. `token` is the lead-scoped signed token
  // from the ?token= query param on /update/<id> — the backend rejects
  // the request without it (M1). Links mailed before that change carry
  // no token and will 403; staff use the team dashboard for those.
  leadPublic: (id, token) => api.get(`/leads/${id}/public`, { params: { token } }),
  leadUpdatePublic: (id, body, token) =>
    api.post(`/leads/${id}/update_public`, body, { params: { token } }),
};
